/**
 * Schema.org Structured Data Generators
 * Generates JSON-LD markup for different content types
 */

const BASE_URL = 'https://whatsnew.vercel.app';

/**
 * Generate WebSite schema
 * Used on homepage to define the website
 */
export const generateWebSiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  'name': 'Apple Dev Timeline',
  'alternateName': "What's New in WWDC",
  'url': BASE_URL,
  'description': 'A comprehensive timeline of iOS, iPadOS, watchOS, tvOS and visionOS features released by Apple year by year.',
  'potentialAction': {
    '@type': 'SearchAction',
    'target': {
      '@type': 'EntryPoint',
      'urlTemplate': `${BASE_URL}/?platform={search_term_string}`
    },
    'query-input': 'required name=search_term_string'
  }
});

/**
 * Generate Organization schema
 * Defines the organization/creator behind the site
 */
export const generateOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  'name': 'Apple Dev Timeline',
  'url': BASE_URL,
  'logo': `${BASE_URL}/favicon-512.png`,
  'foundingDate': '2024',
  'founder': {
    '@type': 'Person',
    'name': 'Rameswar Prasad',
    'jobTitle': 'Apple Platforms Engineer',
    'url': `${BASE_URL}/about`
  },
  'sameAs': [
    'https://github.com/rameswar', // Update with actual social links
    'https://twitter.com/rameswar', // Update with actual social links
    'https://linkedin.com/in/rameswar' // Update with actual social links
  ]
});

/**
 * Generate Person schema for About page
 * Represents the profile information
 */
export const generatePersonSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Person',
  'name': 'Rameswar Prasad',
  'alternateName': 'RP',
  'jobTitle': 'Apple Platforms Engineer',
  'description': 'Polymath with 11+ years building apps for iPhone, iPad, and Apple Watch. Shipped solutions from consumer apps to enterprise IoT.',
  'url': `${BASE_URL}/about`,
  'image': `${BASE_URL}/apple-touch-icon.png`,
  'knowsAbout': [
    'iOS Development',
    'SwiftUI',
    'UIKit',
    'watchOS',
    'iPadOS',
    'Apple Platforms',
    'Mobile Development'
  ],
  'alumniOf': {
    '@type': 'Organization',
    'name': 'Various Tech Companies'
  },
  'worksFor': [
    {
      '@type': 'Organization',
      'name': 'Walmart'
    },
    {
      '@type': 'Organization',
      'name': 'Petasense'
    },
    {
      '@type': 'Organization',
      'name': 'PurpleTalk'
    }
  ]
});

/**
 * Generate ItemList schema for Timeline features
 * Lists all the features displayed on the timeline
 */
export const generateTimelineFeaturesSchema = (features, platform = 'All') => {
  const items = features.slice(0, 10).map((feature, index) => ({
    '@type': 'ListItem',
    'position': index + 1,
    'name': feature.name || feature,
    'description': `${platform} feature released by Apple`
  }));

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    'name': `Apple ${platform} Features Timeline`,
    'description': `Chronological list of ${platform} features released by Apple`,
    'numberOfItems': items.length,
    'itemListElement': items
  };
};

/**
 * Generate ItemList with SoftwareApplication for Toolkit
 * Represents the tools in the indie dev toolkit
 */
export const generateToolkitSchema = (tools) => {
  const toolItems = tools.map((tool, index) => ({
    '@type': 'ListItem',
    'position': index + 1,
    'item': {
      '@type': 'SoftwareApplication',
      'name': tool.name,
      'description': tool.description,
      'url': tool.url,
      'applicationCategory': 'DeveloperApplication',
      'operatingSystem': 'iOS, macOS',
      'offers': {
        '@type': 'Offer',
        'price': (tool.tier || '').toLowerCase() === 'free' ? '0' : 'varies',
        'priceCurrency': 'USD'
      }
    }
  }));

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    'name': 'Indie iOS Developer Toolkit',
    'description': 'Essential tools for every stage of indie iOS app development',
    'numberOfItems': toolItems.length,
    'itemListElement': toolItems
  };
};

/**
 * Generate CollectionPage schema
 * Used for pages that display collections of items
 */
export const generateCollectionPageSchema = (pageName, pageUrl, description) => ({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  'name': pageName,
  'url': `${BASE_URL}${pageUrl}`,
  'description': description,
  'isPartOf': {
    '@type': 'WebSite',
    'name': 'Apple Dev Timeline',
    'url': BASE_URL
  }
});

/**
 * Generate FAQPage schema (for future use)
 * Can be added if you create an FAQ section
 */
export const generateFAQSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  'mainEntity': faqs.map(faq => ({
    '@type': 'Question',
    'name': faq.question,
    'acceptedAnswer': {
      '@type': 'Answer',
      'text': faq.answer
    }
  }))
});
